import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Users,
  ArrowUpRight,
  Check,
  Sparkles,
  TrendingUp,
  Clock,
  Target,
  ChevronDown,
  ChevronUp,
  Zap,
  Shield,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Sparkles, title: 'AI reply drafts', text: 'Zyra suggests replies based on your knowledge base, past conversations, and customer context.' },
  { icon: Zap, title: 'Instant summaries', text: 'One click turns a long thread into a clear, accurate summary.' },
  { icon: Users, title: 'Real-time coaching', text: 'Get feedback on tone, accuracy, and upsell opportunities as you work.' },
  { icon: Target, title: 'Sentiment detection', text: 'Spot frustrated customers early and escalate before churn happens.' },
];

const benefits = [
  { icon: Clock, title: '60% faster replies', description: 'AI drafts help agents respond in seconds instead of minutes.' },
  { icon: Users, title: '+20% higher CSAT', description: 'Consistent, accurate responses improve customer satisfaction.' },
  { icon: TrendingUp, title: '2x faster onboarding', description: 'New agents ramp up quickly with AI coaching and suggestions.' },
  { icon: Shield, title: 'Quality assurance', description: 'Real-time coaching ensures consistent tone and quality across the team.' },
];

const useCases = [
  { title: 'Complex ticket handoff', text: 'A summarized thread lets the next agent pick up in seconds.' },
  { title: 'New agent onboarding', text: 'Coaching and suggested replies shorten ramp time.' },
  { title: 'Upsell and retention', text: 'Zyra flags buying signals and churn risk for agents to act on.' },
  { title: 'Quality monitoring', text: 'Managers get QA insights without extra review cycles.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'Does the AI replace agents?',
    answer: 'No. Zyra is a co-pilot. It drafts, summarizes, and coaches, but humans stay in control of every customer interaction.',
  },
  {
    question: 'Can the AI be trained on our content?',
    answer: 'Yes. Zyra learns from your help center, saved replies, and conversation history so answers match your brand.',
  },
  {
    question: 'How does coaching work?',
    answer: 'Agents see real-time tips on tone, accuracy, and missed opportunities. Managers get aggregate QA and coaching reports.',
  },
  {
    question: 'What languages does the AI support?',
    answer: 'Zyra supports multiple languages and can translate and reply in the customer\'s preferred language.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AgentServicePage() {
  return (
    <>
      <SEO
        title="Agent Service - AI Co-Pilot for Support Agents | ZuriHub"
        description="Give every support agent an AI co-pilot. ZuriHub Agent Service drafts replies, summarizes threads, and coaches agents in real time to improve quality and speed."
        path="/agent-service"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Users className="h-4 w-4" /> Pro & Enterprise
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Turn every agent into your <span className="gradient-text">best agent</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                AI co-pilot that drafts replies, summarizes threads, and coaches agents in real time. Resolve tickets faster with consistent quality.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Users className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/SI-Photo-Partners-02.webp"
                  alt="ZuriHub Agent Service showing AI co-pilot with reply suggestions and coaching"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful AI co-pilot features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything agents need to work faster and smarter.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why teams choose AI co-pilot</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform agent performance.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Open any conversation, get AI suggestions, and send or edit drafts in seconds. Coaching feedback helps agents improve with every interaction.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Users,
                    title: 'Open conversation',
                    description: 'Zyra reads the thread, customer history, and linked articles.',
                  },
                  {
                    icon: Sparkles,
                    title: 'Get suggestions',
                    description: 'Review AI-drafted replies, summaries, and next-best actions.',
                  },
                  {
                    icon: Zap,
                    title: 'Send or edit',
                    description: 'Send the draft as-is or personalize it in seconds.',
                  },
                  {
                    icon: Shield,
                    title: 'Learn and improve',
                    description: 'Coaching feedback helps agents improve with every conversation.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">AI co-pilot features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> AI reply drafts</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> One-click summaries</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Real-time coaching</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Sentiment detection</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Next-best actions</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Brand voice training</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Give every agent an AI co-pilot</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            See how ZuriHub Agent Service improves speed, quality, and CSAT.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about AI co-pilot</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
